import React from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  Divider,
  Avatar,
  Paper,
  Button,
  useMediaQuery,
  useTheme,
  Fade,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import GavelIcon from "@mui/icons-material/Gavel";
import GroupsIcon from "@mui/icons-material/Groups";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { Link } from "react-router-dom";

// Team member data
const teamMembers = [
  {
    name: "Priyanka Saxena",
    position: "Founder | Legal & Financial Consultant",
    description:
      "Priyanka Saxena is the visionary founder of our firm, bringing with her over a decade of rich experience in the fields of law, commerce, and compliance. A dual postgraduate with degrees in LL.B. and M.Com, she combines legal precision with financial expertise to deliver comprehensive solutions for individuals, startups, and established businesses alike.",
    image: "/path-to-image1.jpg",
    specializations: [
      "Business & Company Formation",
      "Taxation & Compliance Services",
      "Intellectual Property Rights",
      "ROC Compliance & Legal Filings",
    ],
  },
  // Other team members commented out
];

// Core values data
const coreValues = [
  {
    title: "Excellence",
    description:
      "We maintain unwavering commitment to excellence in every aspect of our practice, delivering sophisticated legal solutions and exceptional client service that exceed expectations.",
    icon: <EmojiEventsIcon fontSize="large" />,
  },
  {
    title: "Integrity",
    description:
      "Our practice is founded on uncompromising ethical principles, transparency, and dedication to serving our clients' best interests with the highest professional standards.",
    icon: <VerifiedIcon fontSize="large" />,
  },
  {
    title: "Innovation",
    description:
      "We embrace innovative legal strategies and cutting-edge technologies while staying ahead of evolving legal landscapes to provide forward-thinking solutions for our clients.",
    icon: <AccountBalanceIcon fontSize="large" />,
  },
];

// Why choose us data
const whyChooseUs = [
  {
    title: "Client-Centric Approach",
    description:
      "We prioritize our clients' needs and tailor our services to achieve their goals effectively and efficiently.",
  },
  {
    title: "Proven Track Record",
    description:
      "Our history of successful cases and satisfied clients speaks to our dedication and expertise.",
  },
  {
    title: "Comprehensive Expertise",
    description:
      "Our team covers a wide range of legal and financial services, ensuring all your needs are met under one roof.",
  },
];

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: "primary.main",
          color: "white",
          py: { xs: 6, sm: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: 0.05,
            backgroundImage: "url('/path-to-pattern.png')",
            backgroundRepeat: "repeat",
          }}
        />
        <Container maxWidth="lg">
          <Fade in={true} timeout={1000}>
            <Box
              sx={{
                maxWidth: "800px",
                mx: "auto",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                zIndex: 2,
              }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                className="font-heading"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem" },
                  letterSpacing: "-0.02em",
                  textAlign: "center",
                  mb: 2,
                }}
              >
                About Our Firm
              </Typography>
              <Divider
                sx={{
                  width: "80px",
                  height: "4px",
                  backgroundColor: "secondary.main",
                  my: 2,
                }}
              />
              <Typography
                variant="h5"
                className="font-sans"
                sx={{
                  mb: 4,
                  lineHeight: 1.5,
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                  fontWeight: 400,
                  letterSpacing: "0.01em",
                  textAlign: "center",
                  maxWidth: "700px",
                }}
              >
                Excellence in Legal Services Since 2000
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  color="secondary"
                  size={isMobile ? "medium" : "large"}
                  sx={{
                    fontWeight: 600,
                    px: { xs: 3, md: 4 },
                    py: { xs: 1, md: 1.5 },
                    borderRadius: "4px",
                    textTransform: "none",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    boxShadow: "0 4px 14px 0 rgba(0, 0, 0, 0.2)",
                  }}
                >
                  Schedule a Consultation
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Our Legacy Section */}
      <Box sx={{ py: { xs: 5, md: 8 }, bgcolor: "background.default" }}>
        <Container maxWidth="lg">
          <Grid 
            container 
            spacing={{ xs: 4, md: 6 }} 
            sx={{ mb: { xs: 5, md: 8 } }}
            alignItems="center"
          >
            <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                      mb: 2,
                    }}
                  >
                    Our Legacy of Excellence
                  </Typography>
                  <Divider 
                    sx={{ 
                      width: "60px", 
                      height: "3px", 
                      backgroundColor: "primary.main",
                      mb: 3
                    }} 
                  />
                  <Typography 
                    paragraph 
                    sx={{ 
                      fontSize: { xs: "1rem", md: "1.1rem" }, 
                      mb: 3,
                      color: "text.primary" 
                    }}
                  >
                    Established in 2000, our firm has evolved into a preeminent
                    legal institution, serving businesses from emerging startups to
                    Fortune 500 companies. Our commitment to excellence has earned
                    us recognition as one of the nation's leading corporate law
                    firms.
                  </Typography>
                  <Typography 
                    paragraph 
                    sx={{ 
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      color: "text.primary"
                    }}
                  >
                    With a team of distinguished attorneys who combine decades of
                    experience with cutting-edge legal knowledge, we provide
                    comprehensive solutions to complex legal challenges. Our
                    approach integrates deep legal expertise with practical business
                    acumen, enabling us to deliver results that align with our
                    clients' strategic objectives.
                  </Typography>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
              <Fade in={true} timeout={1000} style={{ transitionDelay: "200ms" }}>
                <Paper
                  elevation={3}
                  sx={{ 
                    p: { xs: 3, md: 4 }, 
                    bgcolor: "background.paper", 
                    borderRadius: 3,
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <Typography 
                    variant="h5" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 600,
                      color: "primary.main",
                      fontSize: { xs: "1.25rem", md: "1.5rem" }
                    }}
                  >
                    Firm Overview
                  </Typography>
                  <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <Box 
                        sx={{ 
                          display: "flex", 
                          alignItems: "flex-start", 
                          mb: 3,
                          flexDirection: isMobile ? "column" : "row",
                        }}
                      >
                        <BusinessIcon 
                          color="primary" 
                          sx={{ 
                            mr: isMobile ? 0 : 2,
                            mb: isMobile ? 1 : 0,
                            fontSize: "2rem"
                          }} 
                        />
                        <Typography 
                          variant="body1"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: { xs: "0.95rem", md: "1rem" }
                          }}
                        >
                          25+ Years of Excellence in Legal Services
                        </Typography>
                      </Box>
                      <Box 
                        sx={{ 
                          display: "flex", 
                          alignItems: "flex-start", 
                          mb: 3,
                          flexDirection: isMobile ? "column" : "row",
                        }}
                      >
                        <GavelIcon 
                          color="primary" 
                          sx={{ 
                            mr: isMobile ? 0 : 2,
                            mb: isMobile ? 1 : 0,
                            fontSize: "2rem"
                          }} 
                        />
                        <Typography 
                          variant="body1"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: { xs: "0.95rem", md: "1rem" }
                          }}
                        >
                          Specialized Practice Areas in Corporate & Business Law
                        </Typography>
                      </Box>
                      <Box 
                        sx={{ 
                          display: "flex", 
                          alignItems: "flex-start",
                          flexDirection: isMobile ? "column" : "row",
                        }}
                      >
                        <GroupsIcon 
                          color="primary" 
                          sx={{ 
                            mr: isMobile ? 0 : 2,
                            mb: isMobile ? 1 : 0,
                            fontSize: "2rem"
                          }} 
                        />
                        <Typography 
                          variant="body1"
                          sx={{ 
                            fontWeight: 500,
                            fontSize: { xs: "0.95rem", md: "1rem" }
                          }}
                        >
                          Team of Award-Winning Legal Professionals
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Fade>
            </Grid>
          </Grid>

          {/* Why Choose Us Section */}
          <Box sx={{ my: { xs: 5, md: 8 }, textAlign: "center" }}>
            <Fade in={true} timeout={1000}>
              <Typography
                variant="h4"
                gutterBottom
                align="center"
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                }}
              >
                Why Choose Us
              </Typography>
            </Fade>
            <Fade in={true} timeout={1000} style={{ transitionDelay: "100ms" }}>
              <Divider 
                sx={{ 
                  width: "60px", 
                  height: "3px", 
                  backgroundColor: "primary.main", 
                  mx: "auto",
                  mb: 4
                }}
              />
            </Fade>
            <Grid container spacing={{ xs: 3, md: 4 }}>
              {whyChooseUs.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Fade in={true} timeout={1000} style={{ transitionDelay: `${200 + index * 100}ms` }}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        boxShadow: 2,
                        borderRadius: 3,
                        overflow: "hidden",
                        transition: "transform 0.3s, box-shadow 0.3s",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: 5,
                        },
                      }}
                    >
                      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Typography
                          variant="h5"
                          gutterBottom
                          sx={{ 
                            fontWeight: 600, 
                            textAlign: "center",
                            fontSize: { xs: "1.25rem", md: "1.5rem" },
                            color: "primary.dark"
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Divider sx={{ width: "40px", mx: "auto", my: 2 }} />
                        <Typography
                          variant="body1"
                          sx={{ 
                            textAlign: "center", 
                            mt: 2,
                            fontSize: { xs: "0.95rem", md: "1rem" },
                            color: "text.secondary"
                          }}
                        >
                          {item.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Team Member Section */}
          <Box sx={{ my: { xs: 5, md: 8 }, textAlign: "center" }}>
            <Fade in={true} timeout={1000}>
              <Typography
                variant="h4"
                gutterBottom
                align="center"
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                }}
              >
                Our Distinguished Team
              </Typography>
            </Fade>
            <Fade in={true} timeout={1000} style={{ transitionDelay: "100ms" }}>
              <Divider 
                sx={{ 
                  width: "60px", 
                  height: "3px", 
                  backgroundColor: "primary.main", 
                  mx: "auto",
                  mb: 4
                }}
              />
            </Fade>
            <Fade in={true} timeout={1000} style={{ transitionDelay: "200ms" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: "center",
                  maxWidth: "900px",
                  mx: "auto",
                  borderRadius: 3,
                  overflow: "hidden",
                  bgcolor: "background.paper",
                  boxShadow: 3,
                }}
              >
                <Box
                  sx={{
                    width: { xs: "100%", md: "40%" },
                    height: { xs: "250px", md: "100%" },
                    bgcolor: "primary.main",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 3,
                  }}
                >
                  <Avatar
                    sx={{
                      width: { xs: 150, md: 180 },
                      height: { xs: 150, md: 180 },
                      border: "4px solid white",
                      boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
                    }}
                  >
                    {teamMembers[0].name.charAt(0)}
                  </Avatar>
                </Box>
                <CardContent 
                  sx={{ 
                    p: { xs: 3, md: 4 },
                    width: { xs: "100%", md: "60%" },
                    textAlign: { xs: "center", md: "left" }
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: "1.5rem", md: "1.75rem" },
                    }}
                  >
                    {teamMembers[0].name}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="primary"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    {teamMembers[0].position}
                  </Typography>
                  <Divider sx={{ my: 2, display: { xs: "block", md: "none" } }} />
                  <Typography 
                    variant="body1" 
                    paragraph
                    sx={{ 
                      textAlign: { xs: "center", md: "left" },
                      fontSize: { xs: "0.95rem", md: "1rem" },
                      color: "text.secondary"
                    }}
                  >
                    {teamMembers[0].description}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    color="primary.dark"
                    gutterBottom
                    sx={{ 
                      fontWeight: 700, 
                      mt: 2,
                      fontSize: { xs: "0.9rem", md: "1rem" },
                    }}
                  >
                    Areas of Expertise:
                  </Typography>
                  <Box 
                    component="ul" 
                    sx={{ 
                      pl: 2, 
                      mt: 1, 
                      textAlign: { xs: "center", md: "left" },
                      listStylePosition: "inside",
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1,
                    }}
                  >
                    {teamMembers[0].specializations.map((spec, idx) => (
                      <Typography 
                        key={idx} 
                        component="li" 
                        variant="body2"
                        sx={{ 
                          fontWeight: 500,
                          color: "text.primary",
                          fontSize: { xs: "0.9rem", md: "0.95rem" },
                        }}
                      >
                        {spec}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
              </Box>
            </Fade>
          </Box>

          {/* Core Values Section */}
          <Box sx={{ my: { xs: 5, md: 8 } }}>
            <Fade in={true} timeout={1000}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ 
                  fontWeight: 700, 
                  textAlign: "center", 
                  mb: 2,
                  fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                }}
              >
                Our Core Values
              </Typography>
            </Fade>
            <Fade in={true} timeout={1000} style={{ transitionDelay: "100ms" }}>
              <Divider 
                sx={{ 
                  width: "60px", 
                  height: "3px", 
                  backgroundColor: "primary.main", 
                  mx: "auto",
                  mb: 4
                }}
              />
            </Fade>
            <Grid container spacing={{ xs: 3, md: 4 }}>
              {coreValues.map((value, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Fade in={true} timeout={1000} style={{ transitionDelay: `${200 + index * 100}ms` }}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        boxShadow: 2,
                        borderRadius: 3,
                        overflow: "hidden",
                        transition: "transform 0.3s, box-shadow 0.3s",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: 5,
                        },
                      }}
                    >
                      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Box 
                          sx={{ 
                            display: "flex", 
                            justifyContent: "center", 
                            mb: 2,
                            color: "primary.main" 
                          }}
                        >
                          {React.cloneElement(value.icon, { 
                            sx: { fontSize: "3rem" } 
                          })}
                        </Box>
                        <Typography
                          variant="h5"
                          gutterBottom
                          sx={{ 
                            fontWeight: 600, 
                            textAlign: "center",
                            fontSize: { xs: "1.25rem", md: "1.5rem" },
                            color: "primary.dark"
                          }}
                        >
                          {value.title}
                        </Typography>
                        <Divider sx={{ width: "40px", mx: "auto", my: 2 }} />
                        <Typography
                          variant="body1"
                          sx={{ 
                            textAlign: "center", 
                            mt: 2,
                            fontSize: { xs: "0.95rem", md: "1rem" },
                            color: "text.secondary" 
                          }}
                        >
                          {value.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Call to Action Section */}
          <Fade in={true} timeout={1000}>
            <Box 
              sx={{ 
                my: { xs: 5, md: 8 }, 
                textAlign: "center",
                p: { xs: 3, md: 5 },
                borderRadius: 3,
                backgroundColor: "primary.dark",
                color: "white",
                boxShadow: 4,
              }}
            >
              <Typography 
                variant="h4" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                Ready to Work With Our Expert Team?
              </Typography>
              <Typography 
                variant="body1" 
                paragraph
                sx={{ 
                  maxWidth: "700px", 
                  mx: "auto", 
                  mb: 4,
                  fontSize: { xs: "0.95rem", md: "1.1rem" },
                }}
              >
                Schedule a consultation today and let us help you navigate your legal challenges with confidence and expertise.
              </Typography>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                color="secondary"
                size={isMobile ? "medium" : "large"}
                sx={{
                  fontWeight: 600,
                  px: { xs: 3, md: 4 },
                  py: { xs: 1, md: 1.5 },
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  boxShadow: "0 4px 14px 0 rgba(0, 0, 0, 0.2)",
                }}
              >
                Contact Us Now
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>
    </>
  );
};

export default About;
